import { useEffect, useState } from 'react';
import { fanOutOverlaps, type GeoPin, type PinOffset, type PinPoint } from '../utils/pinCollision';

const NO_OFFSETS: Map<string, PinOffset> = new Map();

function sameLayout(a: Map<string, PinOffset>, b: Map<string, PinOffset>): boolean {
  if (a.size !== b.size) return false;
  for (const [id, offset] of a) {
    const other = b.get(id);
    if (!other || other.dx !== offset.dx || other.dy !== offset.dy) return false;
  }
  return true;
}

/**
 * How far each pin has to step aside so none of them hides another at the
 * current zoom.
 *
 * Two pins overlap or they don't depending on the *pixel* distance between them,
 * which at a fixed zoom is the same wherever the map is panned to. Panning still
 * fires `idle`, so the layout is recomputed then and thrown away when it matches
 * — cheaper than telling pan and zoom apart, and it means dragging the map never
 * re-renders a single pin.
 *
 * Everything here is arithmetic over coordinates we already have: no Places, no
 * Geocoding, no extra map load (ADR-0001).
 */
export function usePinFanOut(
  mapRef: React.RefObject<google.maps.Map | null>,
  points: GeoPin[],
  enabled: boolean,
): Map<string, PinOffset> {
  const [offsets, setOffsets] = useState<Map<string, PinOffset>>(NO_OFFSETS);

  useEffect(() => {
    const map = mapRef.current;
    if (!enabled || !map) return;

    const recompute = () => {
      // The projection is what turns lat/lng into pixels, and it isn't there the
      // instant the map loads — hence `projection_changed` among the triggers.
      const projection = map.getProjection();
      const zoom = map.getZoom();
      if (!projection || zoom == null) return;

      const scale = 2 ** zoom;
      const screen: PinPoint[] = [];
      for (const point of points) {
        const world = projection.fromLatLngToPoint(
          new window.google.maps.LatLng(point.lat, point.lng),
        );
        if (world) screen.push({ id: point.id, x: world.x * scale, y: world.y * scale });
      }

      const next = fanOutOverlaps(screen);
      setOffsets(prev => (sameLayout(prev, next) ? prev : next));
    };

    recompute();
    const listeners = ['zoom_changed', 'projection_changed', 'idle'].map(
      event => window.google.maps.event.addListener(map, event, recompute),
    );
    return () => listeners.forEach(l => window.google.maps.event.removeListener(l));
  }, [mapRef, points, enabled]);

  // Gate on `enabled` rather than clearing on the way out: switching to a view
  // that draws no pins must not leave the other view's layout behind.
  return enabled ? offsets : NO_OFFSETS;
}
